import { addDays, addHours, endOfDay, startOfDay } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- needed for timezone-aware date calculations
import { utcToZonedTime, zonedTimeToUtc } from "date-fns-tz";

interface StartDateOptions {
  /**
   * Whether the shift can be moved to the next day.
   * True when the original shift start + 12 hours extends into the next day.
   * Effectively, shifts starting at noon or later (in facility timezone) can move forward.
   */
  canGoForward: boolean;
  /** Start of the original shift's day (UTC) */
  currentDay: Date;
  /** Start of the day after the original shift's day (UTC) */
  nextDay: Date;
}

/**
 * Calculates whether a shift's start date can be moved to the next day.
 *
 * Uses the same constraint as the legacy calendar view:
 * - maxStartDate = originalShiftStart + 12 hours
 * - canGoForward = start of next day <= end of maxStartDate's day
 *
 * All calculations use the facility timezone to determine day boundaries.
 * Returned dates are in UTC for consistent handling with the rest of the form.
 */
export function getStartDateOptions(originalShiftStart: Date, timezone?: string): StartDateOptions {
  const zonedStart = timezone ? utcToZonedTime(originalShiftStart, timezone) : originalShiftStart;

  const zonedCurrentDay = startOfDay(zonedStart);
  const zonedNextDay = addDays(zonedCurrentDay, 1);
  const zonedMaxStartDate = addHours(zonedStart, 12);
  const canGoForward = zonedNextDay <= endOfDay(zonedMaxStartDate);

  const currentDay = timezone ? zonedTimeToUtc(zonedCurrentDay, timezone) : zonedCurrentDay;
  const nextDay = timezone ? zonedTimeToUtc(zonedNextDay, timezone) : zonedNextDay;

  return { canGoForward, currentDay, nextDay };
}
